import { forwardRef, useEffect, useState } from "react";
import type { ImgHTMLAttributes, ReactNode, SyntheticEvent } from "react";
import { ImageOff } from "lucide-react";

import { cn } from "@/lib/utils";

type NativeImgProps = ImgHTMLAttributes<HTMLImageElement>;

export interface ImageProps extends NativeImgProps {
  /**
   * Resource loading priority hint. React 19 forwards the camelCase spelling
   * to the DOM natively, so this is a plain pass-through now (kept explicit so
   * only valid values type-check).
   */
  fetchPriority?: "high" | "low" | "auto";
  /** Defaults to "async" so images never block the main thread by accident. */
  decoding?: "async" | "sync" | "auto";
  /** Defaults to "lazy" unless the image is marked high priority. */
  loading?: "eager" | "lazy";
  /**
   * Responsive candidate list, e.g. `"hero-800.webp 800w, hero-1600.webp 1600w"`.
   * When present and `sizes` is omitted, a sensible full-width default is used.
   */
  srcSet?: string;
  /** Layout hint for the candidate list. Defaults to "100vw" when srcSet is set. */
  sizes?: string;
  /** Rendered instead of the image when loading fails. Defaults to a muted tile. */
  fallback?: ReactNode;
  /** Extra classes applied to the default fallback tile. */
  fallbackClassName?: string;
}

/**
 * Image: a thin, fully typed wrapper around <img>.
 *
 * Guards the attributes that are easy to get wrong (fetchPriority, decoding,
 * loading, srcSet/sizes) and renders a safe placeholder instead of a broken
 * thumbnail when the resource fails to load.
 */
export const Image = forwardRef<HTMLImageElement, ImageProps>(function Image(
  {
    fetchPriority,
    decoding = "async",
    loading,
    srcSet,
    sizes,
    className,
    alt = "",
    src,
    fallback,
    fallbackClassName,
    onError,
    style,
    width,
    height,
    ...props
  },
  ref,
) {
  const [failed, setFailed] = useState(false);

  // A new source deserves a fresh attempt (theme swaps, retries, list reuse).
  useEffect(() => {
    setFailed(false);
  }, [src, srcSet]);

  const resolvedLoading = loading ?? (fetchPriority === "high" ? "eager" : "lazy");
  const resolvedSizes = sizes ?? (srcSet ? "100vw" : undefined);

  const handleError = (event: SyntheticEvent<HTMLImageElement, Event>) => {
    setFailed(true);
    onError?.(event);
  };

  if (failed) {
    if (fallback !== undefined) return <>{fallback}</>;
    return (
      <div
        role="img"
        aria-label={alt || undefined}
        aria-hidden={alt ? undefined : true}
        style={style}
        className={cn(
          "flex items-center justify-center rounded-md border border-border/60 bg-muted/40 text-muted-foreground",
          className,
          fallbackClassName,
        )}
      >
        <ImageOff className="h-5 w-5 opacity-60" aria-hidden />
      </div>
    );
  }

  return (
    <img
      ref={ref}
      src={src}
      srcSet={srcSet}
      sizes={resolvedSizes}
      alt={alt}
      width={width}
      height={height}
      style={style}
      decoding={decoding}
      loading={resolvedLoading}
      fetchPriority={fetchPriority}
      className={cn(className)}
      onError={handleError}
      {...props}
    />
  );
});

export default Image;
